import React, { useState } from 'react';
import {
  Container,
  Header,
  Input,
  FormField,
  SpaceBetween,
  Button,
  Box,
  Link,
  Icon,
  Spinner,
  Alert,
  ExpandableSection
} from '@cloudscape-design/components';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import config from '../config';

const AIAssistantView = ({ userHomePath, onVideoSelect }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [citations, setCitations] = useState([]);
  const [isAsking, setIsAsking] = useState(false);
  const [error, setError] = useState('');

  const handleAsk = async () => {
    if (!question.trim()) return;

    setIsAsking(true);
    setError('');
    setAnswer('');
    setCitations([]);
    
    try {
      const session = await fetchAuthSession();
      
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.KnowledgeBaseRAGFunctionArn,
        Payload: JSON.stringify({
          query: question,
          maxResults: 5
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));
      
      console.log('RAG response:', result);
      
      if (result.statusCode === 200) {
        const data = JSON.parse(result.body);
        const rawAnswer = data.answer || '';
        // Detect unhelpful AI responses and replace with a friendlier message
        const unhelpfulPatterns = [
          /unable to assist/i,
          /i cannot assist/i,
          /i don't have.*information/i,
          /i couldn't find/i,
          /no relevant.*information/i,
          /i'm not able to/i
        ];
        const isUnhelpful = unhelpfulPatterns.some(p => p.test(rawAnswer));
        if (isUnhelpful || (!rawAnswer.trim() && (!data.citations || data.citations.length === 0))) {
          setAnswer("I couldn't find anything related in your recordings. Try rephrasing your question or using different keywords.");
        } else {
          setAnswer(rawAnswer);
        }
        setCitations(data.citations || []);
      } else {
        throw new Error(result.body || 'Failed to get answer');
      }
    } catch (err) {
      console.error('Error asking AI:', err);
      setError('Failed to get an answer. Please try again.');
    } finally {
      setIsAsking(false);
    }
  };

  const handleRecordingClick = (recordingKey, displayName) => {
    if (recordingKey && onVideoSelect) {
      // Create a video item object to pass to onVideoSelect
      const videoItem = {
        key: recordingKey,
        name: recordingKey.split('/').pop(),
        displayName: displayName || recordingKey.split('/').pop().replace('.webm', ''),
        isVideo: true
      };
      
      onVideoSelect(videoItem);
    }
  };

  return (
    <Container variant='embedded'
      header={
        <Header 
          variant="h1" 
          description="AI scans your entire knowledge base and generates answers with citations from across all your recordings"
        >
          AI Assistant
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Box padding={{ top: 's', bottom: 's' }}>
          <SpaceBetween size="m">
            <FormField
              label={
                <Box variant="strong">
                  💬 What would you like to know?
                </Box>
              }
              description="No manual search needed - just ask and get a comprehensive answer"
              stretch={true}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={question}
                    onChange={({ detail }) => setQuestion(detail.value)}
                    placeholder="e.g., What was discussed about the Q3 budget?"
                    onKeyDown={(e) => e.detail.key === 'Enter' && handleAsk()}
                    disabled={isAsking}
                    type="search"
                    inputMode="search"
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={handleAsk}
                  loading={isAsking}
                  disabled={!question.trim()}
                  iconName="contact"
                >
                  Ask Assistant
                </Button>
              </div>
            </FormField>
          </SpaceBetween>
        </Box>

        {error && (
          <Alert type="warning" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        )}

        {isAsking && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>
              AI is analyzing your recordings...
            </Box>
          </Box>
        )}

        {!isAsking && answer && (
          <SpaceBetween size="m">
            {/* AI Answer */}
            <Container
              header={
                <Header variant="h2">
                  <Icon name="status-positive" variant="success" /> Answer
                </Header>
              }
            >
              <Box variant="p" fontSize="body-m">
                {answer}
              </Box>
            </Container>

            {/* Citations/Sources */}
            {citations.length > 0 && (
              <ExpandableSection
                headerText="Sources"
                variant="container"
                defaultExpanded={false}
                headerCounter={`(${citations.length})`}
              >
                <Box padding={{ top: 'm' }}>
                  <SpaceBetween size="m">
                    {citations.map((citation, index) => (
                      <ExpandableSection
                        key={index}
                        headerText={`Source ${index + 1}`}
                        variant="container"
                      >
                        <SpaceBetween size="m">
                          {citation.citedText && (
                            <Box>
                              <Box variant="strong">Answer section:</Box>
                              <Box variant="p" color="text-body-secondary" padding={{ top: 'xs' }}>
                                "{citation.citedText}"
                              </Box>
                            </Box>
                          )}
                          
                          <Box>
                            <Box variant="strong">Based on:</Box>
                            <Box padding={{ top: 'xs' }}>
                              {citation.recordingKey ? (
                                <Link
                                  variant="primary"
                                  onFollow={() => handleRecordingClick(citation.recordingKey, citation.displayName)}
                                  fontSize="body-m"
                                >
                                  <Icon name="file" /> {citation.displayName}
                                </Link>
                              ) : (
                                <Box color="text-status-inactive">
                                  <Icon name="status-warning" /> {citation.displayName}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </SpaceBetween>
                      </ExpandableSection>
                    ))}
                  </SpaceBetween>
                </Box>
              </ExpandableSection>
            )}
          </SpaceBetween>
        )}

        {!isAsking && !answer && !error && (
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">
              <Icon name="search" size="big" />
            </Box>
            <Box variant="h3" padding={{ top: 's' }}>
              Ask me anything about your recordings
            </Box>
            <Box variant="p" color="text-body-secondary">
              I'll analyze your transcripts and generate a detailed answer with citations
            </Box>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default AIAssistantView;
